import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material'
import CloudIcon from '@mui/icons-material/Cloud'
import GrassIcon from '@mui/icons-material/Grass'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import SeedIcon from '@mui/icons-material/Spa'
import { useScrollReveal } from '../use-scroll-reveal'

const TIMELINE_NODES = [
  {
    date: 'Dec 2025',
    label: 'Project Born',
    icon: <GrassIcon />,
    color: '#375934',
    description:
      'TerraPlanner was conceived as a smarter alternative to spreadsheet-based farm planning — a tool that actually understands seasons, frost windows, and crop lifecycles.',
  },
  {
    date: 'Jan 2026',
    label: 'Proof of Concept',
    icon: <CloudIcon />,
    color: '#5a7d56',
    description:
      'Research phase underway. Integrated CDS ERA5 climate reanalysis data to drive frost-relative crop scheduling. Explored polygon-based field mapping and crop archetype modelling.',
  },
  {
    date: 'Feb 2026',
    label: 'Real-World Testing',
    icon: <SeedIcon />,
    color: '#6b8e65',
    description:
      'Started tracking a real test homestead with actual seeds in the ground. The app began earning its keep — real crops, real schedules, real feedback.',
  },
  {
    date: 'Aug 2026',
    label: 'v1 Release',
    icon: <RocketLaunchIcon />,
    color: '#263d24',
    isGoal: true,
    description:
      'First public release for everyday farmers. A complete, usable tool for planning and tracking seasonal crops — from first frost to harvest.',
  },
]

const TimelineNode = ({ node, index }) => {
  const { ref, visible } = useScrollReveal(0.2)
  const isLeft = index % 2 === 0

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'row', md: isLeft ? 'row' : 'row-reverse' },
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: { xs: 2, md: 3 },
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity 0.55s ease ${index * 0.12}s, transform 0.55s ease ${index * 0.12}s`,
      }}
    >
      {/* Date label — desktop only, outer side */}
      <Box sx={{ flex: '0 0 120px', textAlign: { md: isLeft ? 'right' : 'left' }, display: { xs: 'none', md: 'block' } }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ letterSpacing: 1, textTransform: 'uppercase' }}>
          {node.date}
        </Typography>
        {node.isGoal && (
          <Box mt={0.5}>
            <Chip label="goal" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto' }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: node.color,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 0 0 6px ${node.color}22`,
            flexShrink: 0,
          }}
        >
          {node.icon}
        </Box>
      </Box>

      <Box sx={{ flex: 1, maxWidth: { md: 420 } }}>
        {/* Date — mobile only, above the card */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 0.75 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ letterSpacing: 1, textTransform: 'uppercase' }}>
            {node.date}
          </Typography>
          {node.isGoal && (
            <Box mt={0.5}>
              <Chip label="goal" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
            </Box>
          )}
        </Box>
        <Card elevation={2} sx={{ borderRadius: 3, borderLeft: `4px solid ${node.color}` }}>
          <CardContent sx={{ py: 2, px: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {node.label}
            </Typography>
            <Typography variant="body2" color="text.secondary" lineHeight={1.65}>
              {node.description}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}

export const TimelineSection = () => {
  const { ref, visible } = useScrollReveal(0.1)

  return (
    <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 3, md: 6 }, maxWidth: 900, mx: 'auto' }}>
      <Box
        ref={ref}
        sx={{
          textAlign: 'center',
          mb: 8,
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
        }}
      >
        <Typography variant="overline" color="primary" fontWeight={600} letterSpacing={2}>
          Our Journey
        </Typography>
        <Typography variant="h4" fontWeight={700} mt={0.5}>
          From idea to harvest
        </Typography>
      </Box>

      <Box sx={{ position: 'relative' }}>
        {/* Desktop centre line */}
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            left: '50%',
            top: 0,
            bottom: 0,
            width: 2,
            transform: 'translateX(-50%)',
            background: 'linear-gradient(to bottom, #37593430, #37593480, #37593430)',
            borderRadius: 1,
          }}
        />
        {/* Mobile left-side line — aligned to icon centre (22px) */}
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'absolute',
            left: 21,
            top: 22,
            bottom: 22,
            width: 2,
            background: 'linear-gradient(to bottom, #37593430, #37593480, #37593430)',
            borderRadius: 1,
          }}
        />
        <Stack spacing={{ xs: 3, md: 6 }}>
          {TIMELINE_NODES.map((node, i) => (
            <TimelineNode key={node.date} node={node} index={i} />
          ))}
        </Stack>
      </Box>
    </Box>
  )
}
